import PropTypes from 'prop-types';
import { createFragmentContainer, graphql } from 'react-relay/legacy';

import get from 'lodash.get';
import * as tracking from 'dibs-tracking';
import folderTracking from '../../helpers/storefrontFolderTracking';
import { storefrontEnsureLogin } from '../../mutations/storefrontEnsureLogin';
import authModalLoader from 'dibs-buyer-layout/src/utils/AuthModalLoader';

import { ItemTile } from 'dibs-search-product-tile/exports/ItemTile';
import ItemHeartWrapper from 'dibs-portfolio-heart/exports/ItemHeartWrapper';
import ItemFolderWrapper from 'dibs-portfolio-folder/exports/ItemFolderWrapper';

import styles from './styles/StorefrontProductTile.scss';
import { ServerVarsContextConsumer } from '../../../sharedComponents/ServerVarsContext';

function fireProductFavoriting(action, itemId) {
    let trackObj = {
        action,
        label: 'search/browse',
        category: 'item favoriting',
    };

    if (itemId) {
        trackObj = {
            ...trackObj,
            additional: {
                itemId,
            },
        };
    }

    tracking.trackEvent(trackObj);
}
const StorefrontProductTile = ({
    relay,
    index,
    userId,
    userIds,
    viewer,
    product,
    activeQuickView,
    openQuickView,
    closeQuickView,
    currency,
    showCurrency,
}) => {
    const title = get(product, 'title');
    const image = get(product, 'photos[0].versions[0].webPath');

    const getFavoritesComponent = isMobile => (
        <div className={styles.favoritesWrapper}>
            {/* Heart */}
            <div className={styles.icon}>
                <ItemHeartWrapper
                    authModalShow={cb =>
                        storefrontEnsureLogin({
                            relayEnvironment: relay.environment,
                            authOptions: {
                                ga: {
                                    label: 'save item - dealer storefront page',
                                },
                            },
                        })
                            .then(cb)
                            .catch(() => {})
                    }
                    item={product}
                    loadPortfolioData={!!userId}
                    onFavorited={() => fireProductFavoriting('item added', product.serviceId)}
                    onUnFavorited={() => fireProductFavoriting('item removed', product.serviceId)}
                    viewer={viewer}
                    userId={userId}
                    userIds={userIds}
                />
            </div>
            {/* Folder */}
            {!isMobile && (
                <div className={styles.icon}>
                    <ItemFolderWrapper
                        authModalTrackingLabel="save item - search/browse"
                        item={product}
                        selectedItemImages={image ? [image] : []}
                        selectedItemTitles={title ? [title] : []}
                        shouldFillInFolder={!!userId}
                        singlePortfolioItem
                        trackingHooks={folderTracking}
                        getModalComponent={() => {
                            return storefrontEnsureLogin({
                                relayEnvironment: relay.environment,
                                authOptions: {
                                    shouldPauseAuthFlow: state => state.step === 0,
                                },
                            })
                                .then(() => {
                                    return import(
                                        /* webpackChunkName: "AddToFolderModalWrapper" */
                                        'dibs-portfolio-folder/src/components/AddToFolderModalWrapper'
                                    ).then(exported => exported.default);
                                })
                                .catch(() => {});
                        }}
                        onAddItemSuccess={() => {
                            if (authModalLoader.isPaused()) {
                                authModalLoader.resume();
                            }
                        }}
                        userId={userId}
                        userIds={userIds}
                        viewer={viewer}
                    />
                </div>
            )}
        </div>
    );

    return (
        <ServerVarsContextConsumer>
            {({ isMobile }) => (
                <ItemTile
                    index={index}
                    isMobile={isMobile}
                    item={product}
                    itemSearch={null}
                    itemsPerRow={4}
                    currency={currency}
                    showPrice={!!currency && showCurrency}
                    showSellerName={false}
                    onClick={() => {
                        tracking.trackEcommerce({
                            type: 'productClick',
                            actionField: {
                                list: 'storefront featured listing',
                                itemId: product.serviceId,
                            },
                            products: [product.ecommerceTrackingParams],
                        });
                    }}
                    showQuickView
                    activeQuickView={activeQuickView}
                    closeQuickView={closeQuickView}
                    openQuickView={openQuickView}
                    useLoFiLazyLoader
                    viewer={null}
                    favoritesComponent={getFavoritesComponent(isMobile)}
                    showMSKUSwatch={false}
                    showMeasurements={false}
                    imageSize={isMobile ? 'imageSmall' : 'imageMedium'}
                    hideImagePadding={isMobile}
                />
            )}
        </ServerVarsContextConsumer>
    );
};

StorefrontProductTile.propTypes = {
    openQuickView: PropTypes.func.isRequired,
    closeQuickView: PropTypes.func.isRequired,
    activeQuickView: PropTypes.number,

    relay: PropTypes.object.isRequired,

    // relay variabales
    userId: PropTypes.string,
    userIds: PropTypes.array,
    isTrade: PropTypes.bool,
    currency: PropTypes.string,
    showCurrency: PropTypes.bool,
    index: PropTypes.number.isRequired,

    // relay fragments
    viewer: PropTypes.object.isRequired,
    user: PropTypes.object,
    product: PropTypes.object.isRequired,
};

export default createFragmentContainer(StorefrontProductTile, {
    viewer: graphql`
        fragment StorefrontProductTile_viewer on Viewer {
            ...ItemHeartWrapper_viewer @arguments(loadPortfolioData: $hasUserId, userIds: $userIds)
        }
    `,
    user: graphql`
        fragment StorefrontProductTile_user on User {
            serviceId
        }
    `,
    product: graphql`
        fragment StorefrontProductTile_product on Item {
            ...ItemFolderWrapper_item @arguments(userId: $userId, loadPortfolioData: $hasUserId)
            ...ItemHeartWrapper_item @arguments(userId: $userId, loadPortfolioData: $hasUserId)
            ...ItemTile_item @arguments(isTrade: $isTrade)
            serviceId
            title
            photos {
                versions {
                    webPath
                }
            }
            ecommerceTrackingParams
        }
    `,
});

export { StorefrontProductTile as Raw };
